import React, { useState, useEffect, useRef } from 'react';
import { userApi } from '../../../api/userApi';
import '../CRM.css';

/* =================== USER SEARCH (Assign To / Assigned To) =================== */
const pickList = (data) => {
  const keys = ['content', 'data', 'users', 'records', 'result', 'list'];
  const stack = [data];
  while (stack.length) {
    const cur = stack.pop();
    if (Array.isArray(cur)) return cur;
    if (cur && typeof cur === 'object') keys.forEach((k) => { if (cur[k] != null) stack.push(cur[k]); });
  }
  return [];
};

const userLabel = (u) => (typeof u === 'string' ? u : (u?.name || u?.fullName || u?.username || u?.email || ''));

const contains = (value, q) => String(value || '').toLowerCase().includes(q);

const matchUser = (u, q) =>
  [typeof u === 'string' ? u : (u?.name || u?.fullName), u?.username, u?.email, u?.mobile].some((v) => contains(v, q));

const MIN_SEARCH_CHARS = 2;

/* Typeahead: type in the box, users are fetched only after typing
   at least MIN_SEARCH_CHARS characters. */
const UserSearchInput = ({ label = 'Assign To', value, onChange }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const seqRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => { setText(value || ''); }, [value]);

  /* Fetch users from the API. Short/empty keywords load the full user
     list so the dropdown can be browsed; longer keywords search server-side. */
  const runSearch = (raw) => {
    clearTimeout(timerRef.current);
    const keyword = raw.trim().toLowerCase();
    const seq = ++seqRef.current;
    setLoading(true);
    timerRef.current = setTimeout(() => {
      const params = keyword.length >= MIN_SEARCH_CHARS
        ? { search: keyword, size: 50 }
        : { size: 200 };
      userApi.getAllUsers(params)
        .then((res) => {
          if (seq !== seqRef.current) return;
          setUsers(pickList(res?.data).filter((u) => matchUser(u, keyword)));
        })
        .catch(() => { if (seq === seqRef.current) setUsers([]); })
        .finally(() => { if (seq === seqRef.current) setLoading(false); });
    }, 250);
  };

  const q = text.trim().toLowerCase();
  const results = users
    .map(userLabel)
    .filter((name) => name && (!q || name.toLowerCase().includes(q)));

  const searched = q.length >= MIN_SEARCH_CHARS;

  const pick = (name) => {
    onChange(name);
    setText(name);
    setOpen(false);
    setActive(-1);
  };

  const commit = () => {
    const t = text.trim();
    setOpen(false);
    setActive(-1);
    if (t !== (value || '')) onChange(t);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[active >= 0 ? active : 0];
      if (open && hit) pick(hit);
      else commit();
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className="crm-field">
      <label>{label}</label>
      <div className="crm-user-search">
        <input
          type="text"
          className="crm-user-search-input"
          value={text}
          placeholder="search users..."
          onChange={(e) => { setText(e.target.value); setOpen(true); setActive(-1); runSearch(e.target.value); }}
          onFocus={() => { setOpen(true); setActive(-1); if (!users.length) runSearch(text); }}
          onBlur={commit}
          onKeyDown={onKeyDown}
          autoComplete="off"
          spellCheck="false"
        />
        {text && (
          <button
            type="button"
            className="crm-user-search-clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setText(''); onChange(''); setOpen(true); setActive(-1); runSearch(''); }}
            aria-label="Clear assignee"
            title="Clear"
          >×</button>
        )}
        {open && (
          <div className="crm-user-results" role="listbox" aria-label={`${label} options`}>
            {loading && (
              <div className="crm-user-results-empty">Searching users...</div>
            )}
            {!loading && results.length === 0 && (
              <div className="crm-user-results-empty">
                {searched ? 'No matching users — press Enter to assign the typed name' : 'No users found'}
              </div>
            )}
            {!loading && results.map((name, i) => (
              <div
                key={name + i}
                role="option"
                aria-selected={name === value}
                className={`crm-user-result ${i === active ? 'active' : ''} ${name === value ? 'selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(name)}
              >
                <span className="crm-user-result-name">{name}</span>
                {name === value && <span className="crm-user-result-check" aria-hidden="true">✓</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSearchInput;